import {Navigate, createBrowserRouter} from 'react-router-dom';
import {PricingListRoutePaths, RoutePath} from '$constants/routes';
import {RouteSection, getRoute} from '$utils/route';
import IndexRoute from '$modules/index';
import LoginRoute from '$modules/login';
import ResetPasswordRoute from '$modules/login/ResetPassword';
import AdminRoute from '$modules/admin';
import ProtectedRoute from '$components/ProtectedRoute';
import {UserRoleName} from '$modules/admin/constant';
import StaffCalendarRoute from '$modules/frontdesk/Calendar/CalendarView';
import StaffLayout from '$modules/frontdesk/layout';
import AppointmentsContainer from '$modules/frontdesk/Calendar/Appointments';
import PendingContainer from '$modules/frontdesk/Calendar/PendingRequests';
import BookingDetailsContainer from '$modules/frontdesk/Calendar/BookingDetails';
import ConfirmBooking from '$modules/frontdesk/Calendar/ConfirmBooking';
import RescheduleBooking from '$modules/frontdesk/Calendar/RescheduleBooking';
import BookingScreen from '$modules/booking';
import PatientListView from '$modules/frontdesk/PatientList/PatientListView';
import EditBookingContainer from '$modules/frontdesk/Calendar/EditBooking';
import PatientDetailNotes from '$modules/frontdesk/PatientList/PatientDetails/Notes';
import PatientDetailsContainer from '$modules/frontdesk/PatientList/PatientDetails';
import Notifications from '$modules/frontdesk/Notifications';
import DeleteAppointmentRoute from '$modules/booking/DeleteAppointment';
import AppointmentDeleteSuccessRoute from '$modules/booking/DeleteAppointment/DeleteSuccess';
import Questionnaire from '~/modules/frontdesk/PatientList/PatientDetails/ClinicalData/MedicalHistory/Questionnaire';
import PaymentListContainer from '$modules/frontdesk/Finance/PaymentList';
import PaymentDetailsContainer from '$modules/frontdesk/Finance/PaymentDetails';
import InvoiceDetailsContainer from '~/modules/frontdesk/Finance/InvoiceDetails';
import BookingPaymentDetails from '~/modules/frontdesk/Calendar/BookingDetails/BookingPaymentDetails';
import PaymentSuccess from '$modules/frontdesk/Calendar/BookingDetails/PaymentSuccess';
import FinanceDashboard from '~/modules/frontdesk/Finance/Dashboard';
import PricingListContainer from '~/modules/frontdesk/Finance/Dashboard/PricingList';
import PricingTable from '~/modules/frontdesk/Finance/Dashboard/PricingList/PricingTable';
import FinancialRecordContainer from '~/modules/frontdesk/Finance/FinancialRecord';
import InsuranceClaimsContainer from '~/modules/frontdesk/Finance/InsuranceClaims';
import ClaimDetailsComponent from '~/modules/frontdesk/Finance/InsuranceClaims/ClaimDetails/ClaimDetailsComponent';
import PaymentLinkView from '~/modules/booking/PaymentLinkPage';
import BookingPaymentSummary from '~/modules/booking/PaymentLinkPage/BookingPaymentSummary';

export const router = createBrowserRouter([
  {
    path: RoutePath.indexRoute,
    element: <IndexRoute />,
  },
  {
    path: RoutePath.bookingRoute,
    element: <BookingScreen />,
  },
  {
    path: RoutePath.PayPalCancel,
    element: <BookingScreen />,
  },
  {
    path: RoutePath.loginRoute,
    element: <LoginRoute />,
  },
  {
    path: RoutePath.resetPasswordRoute,
    element: <ResetPasswordRoute />,
  },
  {
    path: RoutePath.deleteAppointmentRoute,
    element: <DeleteAppointmentRoute />,
  },
  {
    path: RoutePath.paymentLinkRoute,
    element: <PaymentLinkView />,
  },
  {
    path: RoutePath.deleteSuccessRoute,
    element: <AppointmentDeleteSuccessRoute />,
  },
  {
    path: RoutePath.adminRoute,
    element: (
      <ProtectedRoute roles={[UserRoleName.Admin]}>
        <AdminRoute />
      </ProtectedRoute>
    ),
  },
  {
    path: RoutePath.staff.index,
    element: (
      <ProtectedRoute
        roles={[
          UserRoleName.Doctor,
          UserRoleName.Frontdesk,
          UserRoleName.Finance,
        ]}
      >
        <StaffLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to={RoutePath.staff.calendar.index} />,
      },
      {
        path: RoutePath.staff.notification,
        element: <Notifications />,
      },
      {
        path: RoutePath.staff.calendar.index,
        children: [
          {
            path: '',
            element: <StaffCalendarRoute />,
          },
          {
            path: RoutePath.staff.calendar.appointments,
            element: <AppointmentsContainer />,
          },
          {
            path: RoutePath.staff.calendar.pendingRequests,
            element: <PendingContainer />,
          },
          {
            path: RoutePath.staff.calendar.newPatientRequests,
            element: <PendingContainer newPatientRequests />,
          },
          {
            path: RoutePath.staff.calendar.bookingDetails + '/:id',
            element: <BookingDetailsContainer />,
          },
          {
            path: RoutePath.staff.calendar.bookingDetails + '/payment/:id',
            element: <BookingPaymentDetails />,
          },
          {
            path: RoutePath.staff.calendar.confirmBooking + '/:id',
            element: <ConfirmBooking />,
          },
          {
            path: RoutePath.staff.calendar.rescheduleBooking + '/:id',
            element: <RescheduleBooking />,
          },
          {
            path: RoutePath.staff.calendar.editBooking + '/:id',
            element: <EditBookingContainer />,
          },
          {
            path: RoutePath.staff.calendar.bookingDetails + '/payment-success',
            element: <PaymentSuccess />,
          },
        ],
      },
      {
        path: RoutePath.staff.patientList.index,
        children: [
          {
            path: '',
            element: <PatientListView />,
          },
          {
            path: ':patientId',

            children: [
              {
                path: '',
                element: <PatientDetailsContainer />,
              },
              {
                path: RoutePath.staff.patientList.questionnaire,
                element: <Questionnaire />,
              },
              {
                path: 'notes',
                element: <PatientDetailNotes overviewSelectedNoteId={null} />,
              },
            ],
          },
        ],
      },
      {
        path: RoutePath.staff.finance.index,
        children: [
          {
            path: '',
            element: (
              <Navigate
                to={getRoute(
                  RouteSection.Finance,
                  RoutePath.staff.finance.dashboard.index
                )}
              />
            ),
          },
          {
            path: RoutePath.staff.finance.paymentList,
            element: <PaymentListContainer />,
          },
          {
            path: RoutePath.staff.finance.paymentSuccess,
            element: <PaymentSuccess />,
          },
          {
            path: RoutePath.staff.finance.paymentDetails,
            element: <PaymentDetailsContainer />,
          },
          {
            path: RoutePath.staff.finance.invoiceDetails,
            element: <InvoiceDetailsContainer />,
          },
          {
            path: RoutePath.staff.finance.financialRecord,
            element: <FinancialRecordContainer />,
          },
          {
            path: RoutePath.staff.finance.insuranceClaims,
            element: <InsuranceClaimsContainer />,
          },
          {
            path: RoutePath.staff.finance.insuranceClaimDetails,
            element: <ClaimDetailsComponent />,
          },
          {
            path: RoutePath.staff.finance.insuranceClaimInvoiceDetails,
            element: <InvoiceDetailsContainer />,
          },
          {
            path: RoutePath.staff.finance.dashboard.index,
            children: [
              {
                path: '',
                element: <FinanceDashboard />,
              },
              {
                path: RoutePath.staff.finance.dashboard.pricingList,
                element: <PricingListContainer />,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={PricingListRoutePaths.consultation}
                        replace
                      />
                    ),
                  },
                  {
                    path: PricingListRoutePaths.consultation,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.diagnostic,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.refractive,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.retinal,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.oculoplastics,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.corneal,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.glaucoma,
                    element: <PricingTable />,
                  },
                  {
                    path: PricingListRoutePaths.addons,
                    element: <PricingTable />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: RoutePath.paypalReturn,
    element: <BookingScreen />,
  },
  {
    path: RoutePath.paymentSuccessRoute,
    element: <BookingPaymentSummary />,
  },
]);
